import { InformationCircleIcon } from '@heroicons/react/24/outline';

import { InfoCallout } from '@/components/ui/info-callout';
// Intentional cross-section import (reuse-first): the top-level Seeds Discovery tab
// re-mounts the shared customer Pius Discovery panel rather than forking a copy
// (relocation tracked in ENG-5005). Do not "fix" this into a seeds-local copy.
import { CustomerDiscoveryTab } from '@/sections/customerDrawer/components/CustomerDiscoveryTab';
import { PiusDisableButton } from '@/sections/customerDrawer/components/PiusDisableButton';

interface SeedsDiscoveryPanelProps {
  /** The currently-viewed tenant's email (impersonated tenant or self). */
  tenantEmail: string;
}

/**
 * Top-level, tenant-wide Pius Discovery panel for the Seeds page (ENG-5078).
 *
 * This is the RELOCATED Discovery surface: it replaces the per-seed drawer
 * Discovery tab (ENG-4994, now removed). It is scoped to the whole tenant, not
 * a single seed — Enable/Disable, Run, Budget, and Jobs all act on the tenant.
 * There is intentionally NO pre-targeting: `CustomerDiscoveryTab` is mounted
 * WITHOUT `initialTargets`, so the Run sub-tab opens with an empty target
 * picker.
 *
 * Rendered only for Praetorian users (gated by the caller in `index.tsx`);
 * `CustomerDiscoveryTab` self-guards on `userType === 'praetorian'` and
 * `PiusDisableButton` renders nothing unless Pius is enabled for the tenant.
 */
export function SeedsDiscoveryPanel({ tenantEmail }: SeedsDiscoveryPanelProps) {
  return (
    <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-1">
      <InfoCallout
        variant="info"
        role="note"
        aria-label="Discovery scope"
        icon={<InformationCircleIcon className="size-5" />}
        data-testid="seeds-discovery-scope-callout"
      >
        <strong>Tenant-wide settings.</strong> Enable/Disable, Run, Budget /
        Limits, and Jobs apply to the entire tenant (
        <strong>{tenantEmail}</strong>), not to individual seeds.
      </InfoCallout>
      {/*
        key={tenantEmail} on this wrapper remounts the ENTIRE Pius subtree — both
        the discovery tab AND the disable button — when a Praetorian switches the
        impersonated tenant in place (the Seeds page is not tenant-keyed at its
        mount site, so this subtree would otherwise persist). Without it,
        CustomerDiscoveryTab's mount-once `selectedSubTab` and ClientRunTab's
        mount-once form state (`selectedTargetMap`, `mode`, `selectedPlugins`)
        would retain the previous tenant's selections, and PiusDisableButton's
        in-flight `isDisabling` mutation flag (not keyed by email) would leak the
        outgoing tenant's disable state onto the incoming tenant until it settled.
        The key must wrap both — keying only CustomerDiscoveryTab left the button
        outside the remount. This mirrors customerDrawer/index.tsx
        `key={customer.email}` and the (now-removed) drawer Discovery tab's
        `key={seed.key}`.
      */}
      <div key={tenantEmail} className="flex flex-1 flex-col gap-4">
        <CustomerDiscoveryTab email={tenantEmail} />
        <PiusDisableButton email={tenantEmail} />
      </div>
    </div>
  );
}
